import React from "react";
import ReactDOM from "react-dom/client";

import Home from "./pages/Home";
import reportWebVitals from "./reportWebVitals";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import "./index.css";
// body font for the game screens that don't set one (Winning, Live). The site
// pages set Cairo through their layouts.
import "./App.css";
import Game from "./pages/Game";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Question from "./pages/Question";
import Winning from "./pages/Winning";
import CustomModeQuestion from "./pages/CustomModeQuestion";
import ModeShare from "./pages/ModeShare";
import GameMenu from "./pages/GameMenu";
import Word from "./pages/Word";
import GiftCode from "./pages/GiftCode";
import PasswordResetRequest from "./pages/PasswordResetRequest";
import PasswordReset from "./pages/PasswordReset";
import Portfolio from "./pages/Portfolio";
import AccountDeletionRequest from "./pages/AccountDeletionRequest";
import AccountDeletionConfirm from "./pages/AccountDeletionConfirm";
import AccountDeletedGoodbye from "./pages/AccountDeletedGoodbye";
import QRCodeGame from "./pages/QRCodeGame";
import ModeCreationTerms from "./pages/ModeCreationTerms";
import Live from "./pages/Live";

// Auth + app shell (new)
import AppProviders from "./AppProviders";
import PartnerLayout from "./contexts/PartnerContext";
import PartnerLanding from "./pages/PartnerLanding";
import ProtectedRoute from "./components/ProtectedRoute";
import AppShell from "./components/AppShell";
import Login from "./pages/Login";
import Register from "./pages/Register";
import GamesList from "./pages/app/GamesList";
import GameCreate from "./pages/app/GameCreate";
import AppGameMenu from "./pages/app/GameMenu";
import CommunityModes from "./pages/app/CommunityModes";
import SearchModes from "./pages/app/SearchModes";
import ModeCreate from "./pages/app/ModeCreate";
import ModeEdit from "./pages/app/ModeEdit";
import Profile from "./pages/app/Profile";
import UserProfile from "./pages/app/UserProfile";
import Pricing from "./pages/Pricing";
import CheckoutCallback from "./pages/CheckoutCallback";
import CampaignRedirect from "./pages/CampaignRedirect";

const queryClient = new QueryClient();

const router = createBrowserRouter([
  {
    // Pathless layout: wraps every route in Auth + Subscription providers.
    // React Router ranks static paths above the dynamic "/:code", so the new
    // /login, /register and /app routes match correctly despite that catch-all.
    element: <AppProviders />,
    children: [
      // ---- Public routes ----
      { path: "/", element: <Home /> },
      { path: "/links", element: <Portfolio /> },
      { path: "/login", element: <Login /> },
      { path: "/register", element: <Register /> },
      { path: "/password-reset-request", element: <PasswordResetRequest /> },
      { path: "/password-reset", element: <PasswordReset /> },
      { path: "/custom-mode/:id/:name/:image", element: <CustomModeQuestion /> },
      // Public landing page for a shared community mode.
      { path: "/m/:id", element: <ModeShare /> },
      { path: "/word/:name/:category", element: <Word /> },
      { path: "/game-menu/:code", element: <GameMenu /> },
      { path: "/question/:id", element: <Question /> },
      { path: "/gift-code/:id", element: <GiftCode /> },
      { path: "/scan", element: <QRCodeGame /> },
      { path: "/mode-creation-terms", element: <ModeCreationTerms /> },
      { path: "/account-deletion-request", element: <AccountDeletionRequest /> },
      { path: "/account-deletion", element: <AccountDeletionConfirm /> },
      { path: "/account-deleted", element: <AccountDeletedGoodbye /> },

      // ---- Authenticated app area ----
      {
        path: "/app",
        element: <ProtectedRoute />,
        children: [
          {
            element: <AppShell />,
            children: [
              { index: true, element: <GamesList /> },
              { path: "games", element: <GamesList /> },
              { path: "games/create", element: <GameCreate /> },
              { path: "games/:id", element: <AppGameMenu /> },
              { path: "games/:id/edit", element: <GameCreate /> },
              { path: "community", element: <CommunityModes /> },
              { path: "community/search", element: <SearchModes /> },
              { path: "modes/create", element: <ModeCreate /> },
              { path: "modes/:id", element: <ModeEdit /> },
              { path: "profile", element: <Profile /> },
              { path: "users/:id", element: <UserProfile /> },
            ],
          },
        ],
      },

      // ---- Premium purchase ----
      // Pricing is public: a shared …/pricing?code=X link shows the discounted
      // prices before login, and paying asks for an account.
      { path: "/pricing", element: <Pricing /> },
      {
        element: <ProtectedRoute />,
        children: [
          { path: "/checkout/callback", element: <CheckoutCallback /> },
        ],
      },

      // ---- Partner co-branded game display ----
      // Same Game/Winning pages, wrapped in PartnerLayout which adds the
      // partner logo to the headers and (premium only) the partner theme.
      {
        path: "/partner/:partnerSlug",
        element: <PartnerLayout />,
        children: [
          { index: true, element: <PartnerLanding /> },
          { path: ":code", element: <Game /> },
          { path: ":code/end", element: <Winning /> },
          { path: "question/:id", element: <Question /> },
        ],
      },

      // ---- Live spectator mirror (static prefix — must stay above /:code) ----
      { path: "/live/:id", element: <Live /> },

      // ---- Campaign links (static prefix — must stay above /:code/end) ----
      // "/go/:slug" and "/:code/end" rank the same, so the earlier sibling
      // wins (a link named "end" must not open a game's end screen).
      { path: "/go/:slug", element: <CampaignRedirect /> },

      // ---- Game display (greedy, keep last) ----
      { path: "/:code", element: <Game /> },
      { path: "/:code/end", element: <Winning /> },

      // Catch-all -> home
      { path: "*", element: <Navigate to="/" /> },
    ],
  },
]);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <ToastContainer rtl />
    </QueryClientProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
